<template>
  <article
    class="card bg-base-100 shadow-xl hover:shadow-2xl transition-shadow duration-300"
  >
    <!-- Featured Image (Strapi v5 compatible) -->
    <figure v-if="getImageUrl(article)" class="aspect-video">
      <img
        :src="getImageUrl(article)"
        :alt="getImageAlt(article)"
        class="w-full h-full object-cover"
      />
    </figure>

    <div class="card-body">
      <!-- Categories -->
      <div class="flex flex-wrap gap-1 mb-2" v-if="getCategoryName(article)">
        <div class="badge badge-outline">
          {{ getCategoryName(article) }}
        </div>
      </div>

      <!-- Title -->
      <h2 class="card-title text-lg mb-2 line-clamp-2">
        {{ getTitle(article) }}
      </h2>

      <!-- Excerpt -->
      <p class="text-base-content/70 text-sm mb-4 line-clamp-3">
        {{ getDescription(article) }}
      </p>

      <!-- Meta -->
      <div
        class="flex items-center justify-between text-sm text-base-content/60 mb-4"
      >
        <span v-if="getArticleAuthor(article)">
          Por {{ getArticleAuthor(article) }}
        </span>
        <time :datetime="getArticlePublishedAt(article)">
          {{ formatDate(getArticlePublishedAt(article)) }}
        </time>
      </div>

      <!-- Read More Button -->
      <div class="card-actions justify-end">
        <NuxtLink :to="`/blog/${getSlug(article)}`" class="btn btn-primary">
          Leer más
        </NuxtLink>
      </div>
    </div>
  </article>
</template>

<script setup>
const props = defineProps({
  article: {
    type: Object,
    required: true,
  },
});

// Strapi v5 compatible helper functions - fields are directly on the object
const getImageUrl = (article) => {
  const config = useRuntimeConfig();
  const strapiUrl =
    config.public.strapiUrl || "https://blog.desertando.anovagroup.es";

  // Strapi v5 uses 'cover' field (direct access)
  const coverImage = article?.cover;

  if (coverImage?.url) {
    // Handle relative URLs
    if (coverImage.url.startsWith("/")) {
      return `${strapiUrl}${coverImage.url}`;
    }
    return coverImage.url;
  }

  // Fallback image
  return "/images/blog-placeholder.jpg";
};

const getImageAlt = (article) => {
  // Strapi v5 direct field access
  const coverImage = article?.cover;
  return coverImage?.alternativeText || getTitle(article) || "Blog post image";
};

const getTitle = (article) => {
  return article?.title || "Sin título";
};

const getDescription = (article) => {
  return article?.description || "Sin descripción";
};

const getSlug = (article) => {
  return article?.slug || "";
};

const getArticleAuthor = (article) => {
  const author = article?.author;
  return author?.name || null;
};

const getArticlePublishedAt = (article) => {
  return article?.publishedAt || article?.createdAt;
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("es-ES", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const getCategoryName = (article) => {
  // Strapi v5 uses singular 'category' field (direct access)
  return article?.category?.name || null;
};

const getCategorySlug = (article) => {
  // Strapi v5 uses singular 'category' field (direct access)
  return article?.category?.slug || null;
};
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
